import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, ResourceRef, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { interval, map, of, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NavigationExtras, Params, Router } from '@angular/router';
import { FormatPipe } from '@app/pipes/format.pipe';
import { SchedulerStepService } from '../step/scheduler-step.service';
import { AppointmentService } from '../appointment/appointment.service';

@Injectable({
  providedIn: 'root'
})
export class Appointment {

  private apiURL: string = environment.apiURL
  private formatPipe = inject(FormatPipe)
  private schedulerService = inject(SchedulerStepService)
  private appointmentService = inject(AppointmentService)
  protected router = inject( Router );
  private appointmentEndpoint: string = environment.appointmentEndPoint
  private checkinEndPoint: string = environment.checkinEndPoint
  private eventsEndPoint: string = environment.eventsEndPoint
  private http = inject(HttpClient)

  private cancel$ = new Subject<void>();
  private heartbeatSub?: Subscription;
  private heartbeatStarted = false;


  branchID = signal<string>('')
  request = signal<number | CheckInPost | null>(null)
  errorInfo = signal<ErrorAppointment | null>(null)

  appointmentGUID = signal<string | null>(null)
  service = {}

  disableAutoNavigation = signal<boolean>(false)
  unhandledError = signal<boolean>(false)
  eventRequest = signal<{ branchID: string , refresh: number} | null>(null)

  eventsResource = rxResource({
    request: () => this.eventRequest(),
    loader: ({ request }) =>
      (request === null)
        ? of(null)
        : this.http.get<TVAppt[]>(
          this.apiURL + this.formatPipe.transform(
            this.eventsEndPoint, {
              branchID: request.branchID
            }
          )).pipe(
            tap(() => this.startHeartbeat())
          )
  });

  startHeartbeat() {
    if (this.heartbeatSub && !this.heartbeatSub.closed) return;
    if (this.heartbeatStarted) return;

    this.cancel$ = new Subject<void>();

    this.heartbeatSub = interval(this.eventRequest()?.refresh)
      .pipe(takeUntil(this.cancel$))
      .subscribe(() => { this.eventsResource.reload() });

    this.heartbeatStarted = true;
  }

  stopHeartbeat() {
    this.cancel$.next();
    if (this.heartbeatSub && !this.heartbeatSub.closed) {
      this.heartbeatSub.unsubscribe();
    }
    this.heartbeatStarted = false;
  }

  checkInResource = rxResource({ 
    request: () => ({
      branchID: this.branchID(),
      customerInfo:  this.request()!
    }),
    loader: ({ request }) => {
      if(this.request() === null) return of (null)
      return this.http.patch<string | CheckInResponse>(
        this.apiURL+this.formatPipe.transform(
          this.checkinEndPoint, 
          {'branchID': request.branchID}
        ), request.customerInfo).pipe(
              map(appointResponse => appointResponse 
            )
      )
    }
  })

  eventsQueue = computed( () => this.eventsResource.value() )

  manageEventResource = rxResource({ 
    request: () => ({
      accessID: this.appointmentGUID()
    }),
    loader: ({ request }) => {
      if (this.appointmentGUID() === null) return of(null);

      return this.http.get<string | CheckInResponse>(this.apiURL + this.appointmentEndpoint + request.accessID).pipe(
        map(appointResponse => {
          return appointResponse;
        })
      );
    }
  })

  error = computed(() => (this.appointmentGUID() === null) ? this.checkInResource.error() as HttpErrorResponse : this.manageEventResource.error() as HttpErrorResponse);

  // Loading state for components to use
  isLoading = computed(() => {
    return (this.appointmentGUID() === null) 
      ? this.checkInResource.isLoading() 
      : this.manageEventResource.isLoading();
  });

  eff = effect( () => {
    if(this.request() !== null ||  this.appointmentGUID() != null) {
      // Reset unhandled error when starting new request
      this.unhandledError.set(false)
      
      if(!this.checkInResource.isLoading() && this.appointmentGUID() === null){
        if(this.error()){
          if(this.error().status === 409){
            const errorJSON: ErrorCheckIn = this.error().error

            const appointmentDate = new Date(errorJSON.eventTime);
            const adjustedTime = appointmentDate.getTime() - appointmentDate.getTimezoneOffset() * 60 * 1000;
            const adjustedDate = new Date(adjustedTime);
            const name = errorJSON.fullName
            const now = new Date();
            const isToday =
              adjustedDate.getFullYear() === now.getFullYear() &&
              adjustedDate.getMonth() === now.getMonth() &&
              adjustedDate.getDate() === now.getDate();

            const time = adjustedDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            }).toLowerCase().replaceAll(' ', '');

            const month = String(adjustedDate.getMonth() + 1).padStart(2, '0') // getMonth() is zero-based
            const day = String(adjustedDate.getDate()).padStart(2, '0')

            const datePart = !isToday ? `${month}/${day}` : '';
            const todayLabel = isToday ? 'today' : '';

            this.errorInfo.update( (_) => {
              return {
                time: `${datePart ? datePart + ' at ' : ''}${time} ${todayLabel}`.trim(),
                firstname: (name !== null) ? name.split(' ')[0] : '',
                branch: errorJSON.branch.LocationName
              }
            })
            
            if (errorJSON.error.includes('Appointment is scheduled for a different day.')){
              this.router.navigate([`/information/wrongDate`], {queryParamsHandling: 'merge', replaceUrl: true})
            }else if (errorJSON.error.includes('Appointment is scheduled at a different branch.')){
              this.router.navigate([`/information/wrongLocation`], {queryParamsHandling: 'merge', replaceUrl: true})
            } else if (errorJSON.error.includes('It is too early to check into the closest appointment.')){
              this.router.navigate([`/information/wrongTimeEarly`], {queryParamsHandling: 'merge', replaceUrl: true})
            } else if (errorJSON.error.includes('It is too late to check into the closest appointment.')){
              this.router.navigate([`/information/wrongTimeLate`], {queryParamsHandling: 'merge', replaceUrl: true})
            }

          }else if (this.error().status === 500) {
            if (!this.disableAutoNavigation()) {
              this.router.navigate([`/information/server`], {queryParamsHandling: 'merge', replaceUrl: true})
            } else {
              this.unhandledError.set(true)
            }
          } else {
            // Any other unhandled error
            if (!this.disableAutoNavigation()) {
              this.router.navigate([`/information/server`], {queryParamsHandling: 'merge', replaceUrl: true})
            } else {
              this.unhandledError.set(true)
            }
          }
        } else if(!this.checkInResource.isLoading() && this.checkInResource.value() !== null &&  this.schedulerService.payload()['contact-info'] === undefined){
    
          let queryParams: Params = {
            origin: 'check-in',
          }
  
          if((this.checkInResource.value() as CheckInResponse).early)
            queryParams = {...queryParams, early:true}
  
          if((this.checkInResource.value() as CheckInResponse).late)
            queryParams = {...queryParams, late:true}

            this.manageAppointment(this.checkInResource as ResourceRef<CheckInResponse>,{queryParams: queryParams, queryParamsHandling: 'merge'})
        
        }
      } else if (!this.manageEventResource.isLoading()) {
        if(!this.manageEventResource.isLoading() && this.manageEventResource.value() !== null &&  this.schedulerService.payload()['contact-info'] === undefined){
          if(this.error()){
            if( this.error().status === 404)
              this.router.navigate([`/information/expired`], {queryParamsHandling: 'merge', replaceUrl: true})
            else if (this.error().status === 500) {
              this.router.navigate([`/information/server`], {queryParamsHandling: 'merge', replaceUrl: true})
            }
          }else {
            this.manageAppointment(this.manageEventResource as ResourceRef<CheckInResponse>, {})
          }
        }
      }
    }
  })

  manageAppointment (resource:  ResourceRef<CheckInResponse>, extra: NavigationExtras) {
    this.schedulerService.branchSelected.set((resource.value()).branch)
    this.schedulerService.setPayload('contact-info', {
      fullName: (resource.value() as CheckInResponse).customer.customerName,
      email: (resource.value() as CheckInResponse).customer.email,
      membershipNumber: (resource.value() as CheckInResponse).customer.memberNumber,
      phone: (resource.value() as CheckInResponse).customer.phoneNumber,
    } as any )

    this.schedulerService.setPayload('services', {description: (resource.value() as CheckInResponse).service.name} )

    this.schedulerService.setPayload('category', resource.value().service)

    if (resource.value().scheduledStart) {
        let startDate:any = new Date(resource.value().scheduledStart!).getTime() - new Date().getTimezoneOffset() * 60 * 1000
        startDate = new Date(startDate)

        const formattedDate = startDate.toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: 'numeric' 
        });
        const formattedTime = startDate.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit', 
          hour12: true 
        });
        
       
        this.schedulerService.payload.set({
          ...this.schedulerService.payload(),
          calendar: `${formattedDate} @ ${formattedTime}`
        });
    }
  

    this.appointmentService.appointmentID.set((resource.value() as CheckInResponse).eventID)
    this.router.navigate([`/your-appointment`, (this.appointmentGUID() !== null) ? this.appointmentGUID() : (resource.value() as CheckInResponse).eventID], extra)
  }

}
